/**
 * TIER 3 — TOKENS DE DOMINIO
 * ---------------------------------------------------------------------------
 * El vocabulario visual propio de este CRM: estados de ticket, prioridades,
 * canales de contacto, semáforo de KPI y segmento de cliente.
 *
 * Cada entrada trae `bg` / `fg` / `border` / `dot` para las dos resoluciones,
 * de modo que un `<StatusBadge status="escalado" />` se pinte idéntico en la
 * web y en la app sin que ninguna de las dos decida colores por su cuenta.
 *
 * Accesibilidad: el color NUNCA es el único portador de significado. Cada
 * token expone además `icon` (nombre lucide) y una etiqueta en español, y los
 * componentes deben renderizar ambos.
 */

import { amber, blue, green, neutral, orange, red, teal, violet, pink } from './primitives';
import type { ColorScheme } from './semantic';

export interface ToneTokens {
  /** Fondo del badge/chip. */
  bg: string;
  /** Texto e icono del badge. */
  fg: string;
  /** Borde del badge (1px). */
  border: string;
  /** Punto/indicador sólido y color de la serie en gráficos. */
  dot: string;
}

export type ToneByScheme = Record<ColorScheme, ToneTokens>;

const tone = (
  light: [bg: string, fg: string, border: string, dot: string],
  dark: [bg: string, fg: string, border: string, dot: string],
): ToneByScheme => ({
  light: { bg: light[0], fg: light[1], border: light[2], dot: light[3] },
  dark: { bg: dark[0], fg: dark[1], border: dark[2], dot: dark[3] },
});

// ── Estados de ticket (RF-02) ───────────────────────────────────────────────
// Seis estados con tonos distinguibles. `pendiente` y `cerrado` comparten
// familia neutra a propósito pero se diferencian por tratamiento: `pendiente`
// va relleno y `cerrado` va en contorno, además del icono.

export type TicketStatus =
  | 'nuevo'
  | 'enProceso'
  | 'pendiente'
  | 'escalado'
  | 'resuelto'
  | 'cerrado';

export interface StatusMeta {
  label: string;
  /** Nombre del icono en lucide-react / lucide-react-native. */
  icon: string;
  /** `solid` = relleno; `outline` = sólo borde. */
  variant: 'solid' | 'outline';
  tone: ToneByScheme;
  /** Orden natural en tableros y selectores. */
  order: number;
  /** Si el ticket sigue consumiendo SLA en este estado. */
  countsTowardSla: boolean;
}

export const ticketStatus: Record<TicketStatus, StatusMeta> = {
  nuevo: {
    label: 'Nuevo',
    icon: 'Inbox',
    variant: 'solid',
    order: 1,
    countsTowardSla: true,
    tone: tone(
      [blue[50], blue[800], blue[200], blue[600]],
      ['rgba(96,165,250,0.16)', blue[300], 'rgba(96,165,250,0.34)', blue[400]],
    ),
  },
  enProceso: {
    label: 'En proceso',
    icon: 'LoaderCircle',
    variant: 'solid',
    order: 2,
    countsTowardSla: true,
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
  pendiente: {
    label: 'Pendiente',
    icon: 'PauseCircle',
    variant: 'solid',
    order: 3,
    // Pendiente = a la espera del cliente o de un tercero: el reloj se detiene.
    countsTowardSla: false,
    tone: tone(
      [neutral[100], neutral[700], neutral[300], neutral[500]],
      ['#202227', neutral[300], neutral[700], neutral[400]],
    ),
  },
  escalado: {
    label: 'Escalado',
    icon: 'TriangleAlert',
    variant: 'solid',
    order: 4,
    countsTowardSla: true,
    tone: tone(
      [violet[50], violet[800], violet[200], violet[600]],
      ['rgba(167,139,250,0.16)', violet[300], 'rgba(167,139,250,0.34)', violet[400]],
    ),
  },
  resuelto: {
    label: 'Resuelto',
    icon: 'CircleCheck',
    variant: 'solid',
    order: 5,
    countsTowardSla: false,
    tone: tone(
      [green[50], green[800], green[200], green[600]],
      ['rgba(63,184,124,0.16)', green[300], 'rgba(63,184,124,0.34)', green[400]],
    ),
  },
  cerrado: {
    label: 'Cerrado',
    icon: 'Archive',
    variant: 'outline',
    order: 6,
    countsTowardSla: false,
    tone: tone(
      // El punto va un paso mas oscuro que el de `pendiente`: comparten familia
      // neutra, pero en un grafico de tickets por estado tienen que ser dos
      // colores distintos, no uno.
      ['transparent', neutral[600], neutral[300], neutral[600]],
      ['transparent', neutral[400], neutral[700], neutral[500]],
    ),
  },
};

// ── Prioridad ───────────────────────────────────────────────────────────────

export type Priority = 'alta' | 'media' | 'baja';

export interface PriorityMeta {
  label: string;
  icon: string;
  tone: ToneByScheme;
  order: number;
  /** Horas de SLA para primera respuesta según prioridad (meta TPA < 4h). */
  firstResponseHours: number;
  /** Horas de SLA para resolución (meta MTTR < 24h). */
  resolutionHours: number;
}

export const priority: Record<Priority, PriorityMeta> = {
  alta: {
    label: 'Alta',
    icon: 'ChevronsUp',
    order: 1,
    firstResponseHours: 1,
    resolutionHours: 8,
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
  media: {
    label: 'Media',
    icon: 'Equal',
    order: 2,
    firstResponseHours: 4,
    resolutionHours: 24,
    tone: tone(
      [orange[50], orange[800], orange[200], orange[600]],
      ['rgba(251,146,60,0.16)', orange[300], 'rgba(251,146,60,0.34)', orange[400]],
    ),
  },
  baja: {
    label: 'Baja',
    icon: 'ChevronsDown',
    order: 3,
    firstResponseHours: 8,
    resolutionHours: 72,
    tone: tone(
      [neutral[100], neutral[700], neutral[300], neutral[500]],
      ['#202227', neutral[300], neutral[700], neutral[400]],
    ),
  },
};

// ── Tipo de ticket ──────────────────────────────────────────────────────────

export type TicketType = 'reclamo' | 'garantia' | 'consultaTecnica' | 'repuesto' | 'seguimiento';

export const ticketType: Record<TicketType, { label: string; icon: string; tone: ToneByScheme }> = {
  reclamo: {
    label: 'Reclamo',
    icon: 'MessageSquareWarning',
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
  garantia: {
    label: 'Garantía',
    icon: 'ShieldCheck',
    tone: tone(
      [violet[50], violet[800], violet[200], violet[600]],
      ['rgba(167,139,250,0.16)', violet[300], 'rgba(167,139,250,0.34)', violet[400]],
    ),
  },
  consultaTecnica: {
    label: 'Consulta técnica',
    icon: 'Wrench',
    tone: tone(
      [blue[50], blue[800], blue[200], blue[600]],
      ['rgba(96,165,250,0.16)', blue[300], 'rgba(96,165,250,0.34)', blue[400]],
    ),
  },
  repuesto: {
    label: 'Repuesto',
    icon: 'Cog',
    tone: tone(
      [teal[50], teal[800], teal[200], teal[600]],
      ['rgba(45,212,191,0.16)', teal[300], 'rgba(45,212,191,0.34)', teal[400]],
    ),
  },
  seguimiento: {
    label: 'Seguimiento',
    icon: 'CalendarClock',
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
};

// ── Canales de contacto (RF-04, RF-05) ──────────────────────────────────────
// Los canales de terceros conservan su color de marca: es lo que el usuario
// reconoce de un vistazo en la bandeja unificada.

export type Channel =
  | 'whatsapp'
  | 'facebook'
  | 'messenger'
  | 'email'
  | 'telefono'
  | 'web'
  | 'presencial'
  | 'sistema';

export const channel: Record<Channel, { label: string; icon: string; brand: string; tone: ToneByScheme }> = {
  whatsapp: {
    label: 'WhatsApp',
    icon: 'MessageCircle',
    brand: '#25D366',
    tone: tone(
      ['#E8F9EE', '#0B6B33', '#B5EBC9', '#0E8A3E'],
      ['rgba(37,211,102,0.16)', '#7FE3A6', 'rgba(37,211,102,0.34)', '#25D366'],
    ),
  },
  facebook: {
    label: 'Facebook',
    // lucide 1.x retiro los iconos de marca, asi que `Facebook` ya no existe
    // en lucide-react NI en lucide-react-native. El pulgar es la iconografia
    // propia de la plataforma y, junto a la etiqueta, no se presta a confusion.
    // Se fija aqui para que web y movil sustituyan igual y no cada una a su modo.
    icon: 'ThumbsUp',
    brand: '#1877F2',
    tone: tone(
      ['#EAF2FE', '#0B4FA8', '#BBD7FB', '#1877F2'],
      ['rgba(24,119,242,0.18)', '#8FBEFB', 'rgba(24,119,242,0.36)', '#4A94F5'],
    ),
  },
  messenger: {
    label: 'Messenger',
    icon: 'Send',
    brand: '#0084FF',
    tone: tone(
      ['#E6F3FF', '#00539E', '#B3DAFF', '#0084FF'],
      ['rgba(0,132,255,0.18)', '#7FC2FF', 'rgba(0,132,255,0.36)', '#38A3FF'],
    ),
  },
  email: {
    label: 'Correo',
    icon: 'Mail',
    brand: violet[600],
    tone: tone(
      [violet[50], violet[800], violet[200], violet[600]],
      ['rgba(167,139,250,0.16)', violet[300], 'rgba(167,139,250,0.34)', violet[400]],
    ),
  },
  telefono: {
    label: 'Teléfono',
    icon: 'Phone',
    brand: teal[600],
    tone: tone(
      [teal[50], teal[800], teal[200], teal[600]],
      ['rgba(45,212,191,0.16)', teal[300], 'rgba(45,212,191,0.34)', teal[400]],
    ),
  },
  web: {
    label: 'Web',
    icon: 'Globe',
    brand: neutral[500],
    tone: tone(
      [neutral[100], neutral[700], neutral[300], neutral[500]],
      ['#202227', neutral[300], neutral[700], neutral[400]],
    ),
  },
  presencial: {
    label: 'Presencial',
    icon: 'Store',
    brand: orange[500],
    tone: tone(
      [orange[50], orange[800], orange[200], orange[600]],
      ['rgba(251,146,60,0.16)', orange[300], 'rgba(251,146,60,0.34)', orange[400]],
    ),
  },
  sistema: {
    label: 'Sistema',
    icon: 'Bot',
    brand: pink[600],
    tone: tone(
      [pink[50], pink[800], pink[200], pink[600]],
      ['rgba(244,114,182,0.16)', pink[300], 'rgba(244,114,182,0.34)', pink[400]],
    ),
  },
};

// ── Semáforo de KPI ─────────────────────────────────────────────────────────
// Se aplica a TPA, AHT, FCR, MTTR, NRP, retención, recompra y churn.

export type KpiStatus = 'cumple' | 'riesgo' | 'incumple' | 'sinDatos';

export const kpiStatus: Record<KpiStatus, { label: string; icon: string; tone: ToneByScheme }> = {
  cumple: {
    label: 'Cumple',
    icon: 'CircleCheck',
    tone: tone(
      [green[50], green[800], green[200], green[600]],
      ['rgba(63,184,124,0.16)', green[300], 'rgba(63,184,124,0.34)', green[400]],
    ),
  },
  riesgo: {
    label: 'En riesgo',
    icon: 'CircleAlert',
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
  incumple: {
    label: 'No cumple',
    icon: 'CircleX',
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
  sinDatos: {
    label: 'Sin datos',
    icon: 'CircleDashed',
    tone: tone(
      [neutral[100], neutral[600], neutral[200], neutral[500]],
      ['#202227', neutral[400], neutral[800], neutral[500]],
    ),
  },
};

/** Dirección "buena" de un indicador: sirve para pintar deltas y flechas. */
export type TrendDirection = 'up' | 'down' | 'flat';

export const trend = {
  /** Un delta positivo es bueno (FCR, retención, recompra). */
  positiveIsGood: ['fcr', 'retencion', 'recompra', 'clv', 'satisfaccion'] as const,
  /** Un delta positivo es malo (TPA, AHT, MTTR, NRP, churn). */
  positiveIsBad: ['tpa', 'aht', 'mttr', 'nrp', 'churn'] as const,
};

// ── Segmentación de clientes por volumen de compra (RF-01) ──────────────────

export type CustomerTier = 'bronce' | 'plata' | 'oro' | 'platino';

export const customerTier: Record<CustomerTier, { label: string; icon: string; order: number; tone: ToneByScheme }> = {
  bronce: {
    label: 'Bronce',
    icon: 'Medal',
    order: 1,
    tone: tone(
      ['#FBF1E8', '#7A4A1F', '#EBD2B8', '#A97142'],
      ['rgba(169,113,66,0.20)', '#D9A87A', 'rgba(169,113,66,0.40)', '#C08A57'],
    ),
  },
  plata: {
    label: 'Plata',
    icon: 'Medal',
    order: 2,
    tone: tone(
      [neutral[100], neutral[700], neutral[300], neutral[500]],
      ['#202227', neutral[300], neutral[700], neutral[400]],
    ),
  },
  oro: {
    label: 'Oro',
    icon: 'Crown',
    order: 3,
    tone: tone(
      [amber[50], amber[800], amber[200], '#B8860B'],
      ['rgba(212,160,23,0.18)', amber[300], 'rgba(212,160,23,0.36)', '#E0B233'],
    ),
  },
  platino: {
    label: 'Platino',
    icon: 'Gem',
    order: 4,
    tone: tone(
      [violet[50], violet[800], violet[200], violet[600]],
      ['rgba(167,139,250,0.16)', violet[300], 'rgba(167,139,250,0.34)', violet[400]],
    ),
  },
};

// ── Riesgo de abandono (churn) ──────────────────────────────────────────────

export type ChurnRisk = 'bajo' | 'medio' | 'alto' | 'critico';

export const churnRisk: Record<ChurnRisk, { label: string; icon: string; order: number; tone: ToneByScheme }> = {
  bajo: {
    label: 'Riesgo bajo',
    icon: 'ShieldCheck',
    order: 1,
    tone: tone(
      [green[50], green[800], green[200], green[600]],
      ['rgba(63,184,124,0.16)', green[300], 'rgba(63,184,124,0.34)', green[400]],
    ),
  },
  medio: {
    label: 'Riesgo medio',
    icon: 'ShieldAlert',
    order: 2,
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
  alto: {
    label: 'Riesgo alto',
    icon: 'ShieldX',
    order: 3,
    tone: tone(
      [orange[50], orange[800], orange[200], orange[600]],
      ['rgba(251,146,60,0.16)', orange[300], 'rgba(251,146,60,0.34)', orange[400]],
    ),
  },
  critico: {
    label: 'Riesgo crítico',
    icon: 'OctagonAlert',
    order: 4,
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
};

// ── Estado de garantía (RF-07) ──────────────────────────────────────────────

export type WarrantyStatus = 'vigente' | 'porVencer' | 'vencida' | 'anulada';

export const warrantyStatus: Record<WarrantyStatus, { label: string; icon: string; tone: ToneByScheme }> = {
  vigente: {
    label: 'Vigente',
    icon: 'ShieldCheck',
    tone: tone(
      [green[50], green[800], green[200], green[600]],
      ['rgba(63,184,124,0.16)', green[300], 'rgba(63,184,124,0.34)', green[400]],
    ),
  },
  porVencer: {
    label: 'Por vencer',
    icon: 'Clock',
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
  vencida: {
    label: 'Vencida',
    icon: 'ShieldOff',
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
  anulada: {
    label: 'Anulada',
    icon: 'Ban',
    tone: tone(
      ['transparent', neutral[600], neutral[300], neutral[500]],
      ['transparent', neutral[400], neutral[700], neutral[500]],
    ),
  },
};

// ── Estado de campaña / promoción masiva ────────────────────────────────────

export type CampaignStatus = 'borrador' | 'programada' | 'enviando' | 'enviada' | 'fallida' | 'cancelada';

export const campaignStatus: Record<CampaignStatus, { label: string; icon: string; tone: ToneByScheme }> = {
  borrador: {
    label: 'Borrador',
    icon: 'FilePen',
    tone: tone(
      [neutral[100], neutral[700], neutral[300], neutral[500]],
      ['#202227', neutral[300], neutral[700], neutral[400]],
    ),
  },
  programada: {
    label: 'Programada',
    icon: 'CalendarClock',
    tone: tone(
      [blue[50], blue[800], blue[200], blue[600]],
      ['rgba(96,165,250,0.16)', blue[300], 'rgba(96,165,250,0.34)', blue[400]],
    ),
  },
  enviando: {
    label: 'Enviando',
    icon: 'Send',
    tone: tone(
      [amber[50], amber[800], amber[200], amber[600]],
      ['rgba(245,173,35,0.16)', amber[300], 'rgba(245,173,35,0.34)', amber[400]],
    ),
  },
  enviada: {
    label: 'Enviada',
    icon: 'CircleCheck',
    tone: tone(
      [green[50], green[800], green[200], green[600]],
      ['rgba(63,184,124,0.16)', green[300], 'rgba(63,184,124,0.34)', green[400]],
    ),
  },
  fallida: {
    label: 'Fallida',
    icon: 'CircleX',
    tone: tone(
      [red[50], red[800], red[200], red[600]],
      ['rgba(239,68,68,0.16)', red[300], 'rgba(239,68,68,0.34)', red[500]],
    ),
  },
  cancelada: {
    label: 'Cancelada',
    icon: 'Ban',
    tone: tone(
      // En contorno, como `cerrado`: una campana cancelada es historia, no un
      // estado que reclame atencion.
      ['transparent', neutral[600], neutral[300], neutral[600]],
      ['transparent', neutral[400], neutral[700], neutral[500]],
    ),
  },
};

// ── Roles del sistema ───────────────────────────────────────────────────────

export type UserRole = 'admin' | 'supervisor' | 'asesor' | 'tecnico' | 'lectura';

export const userRole: Record<UserRole, { label: string; icon: string; description: string }> = {
  admin: {
    label: 'Administrador CRM',
    icon: 'ShieldUser',
    description: 'Configuración, gestión de usuarios y reportes.',
  },
  supervisor: {
    label: 'Supervisor',
    icon: 'ChartNoAxesCombined',
    description: 'Supervisa KPIs, reasigna tickets y aprueba escalamientos.',
  },
  asesor: {
    label: 'Asesor de atención',
    icon: 'Headset',
    description: 'Gestión de tickets y seguimiento postventa.',
  },
  tecnico: {
    label: 'Soporte técnico',
    icon: 'Wrench',
    description: 'Resolución de incidencias y registro de soluciones.',
  },
  lectura: {
    label: 'Solo lectura',
    icon: 'Eye',
    description: 'Consulta de información y tableros, sin edición.',
  },
};

export const domainTokens = {
  ticketStatus,
  ticketType,
  priority,
  channel,
  kpiStatus,
  customerTier,
  churnRisk,
  warrantyStatus,
  campaignStatus,
  userRole,
  trend,
} as const;
